/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type MatrixClient, type Room as MatrixRoom } from "matrix-js-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type RecordingNotice } from "../callrecorder";
import {
  type ProtocolistFailureReason,
  type ProtocolistNotice,
} from "../protocolist";

/**
 * Расшифровка отказа для человека. Ключи перечислены явно, а не собираются
 * из кода причины: так i18next проверяет их на этапе сборки и в звонке не
 * всплывёт сырой `protocolist.unavailable_reason.…` вместо русской фразы.
 */
const protocolistUnavailableReasonKeys = {
  backend_rejected: "protocolist.unavailable_reason.backend_rejected",
  backend_unreachable: "protocolist.unavailable_reason.backend_unreachable",
  guest_forbidden: "protocolist.unavailable_reason.guest_forbidden",
  room_name_missing: "protocolist.unavailable_reason.room_name_missing",
} as const satisfies Record<ProtocolistFailureReason, string>;

type UseRecordingNoticeResult = {
  recordingNoticeText: string | null;
  recordingNoticeVariant: "start" | "stop";
  onRecordingNotice: (notice: RecordingNotice) => void;
  onProtocolistNotice: (notice: ProtocolistNotice) => void;
};

/**
 * Управляет текстом уведомления о старте/остановке записи и его авто-скрытием.
 */
export function useRecordingNotice(
  client: MatrixClient,
  matrixRoom: MatrixRoom,
): UseRecordingNoticeResult {
  const { t } = useTranslation();
  const [recordingNoticeText, setRecordingNoticeText] = useState<string | null>(
    null,
  );
  const [recordingNoticeVariant, setRecordingNoticeVariant] = useState<
    "start" | "stop"
  >("start");
  const recordingNoticeTimeoutRef = useRef<number | null>(null);

  const clearRecordingNoticeTimeout = useCallback((): void => {
    if (recordingNoticeTimeoutRef.current !== null) {
      window.clearTimeout(recordingNoticeTimeoutRef.current);
      recordingNoticeTimeoutRef.current = null;
    }
  }, []);

  const showRecordingNotice = useCallback(
    (text: string): void => {
      clearRecordingNoticeTimeout();
      setRecordingNoticeText(text);
      recordingNoticeTimeoutRef.current = window.setTimeout(() => {
        setRecordingNoticeText(null);
        recordingNoticeTimeoutRef.current = null;
      }, 5000);
    },
    [clearRecordingNoticeTimeout],
  );

  useEffect(() => {
    return (): void => {
      clearRecordingNoticeTimeout();
    };
  }, [clearRecordingNoticeTimeout]);

  const resolveRecordingSenderLabel = useCallback(
    (sender: string | null): string => {
      if (!sender) {
        return t("call_recorder.notice_someone");
      }

      if (sender === client.getUserId()) {
        return t("call_recorder.notice_you");
      }

      return matrixRoom.getMember(sender)?.name ?? sender;
    },
    [client, matrixRoom, t],
  );

  const onRecordingNotice = useCallback(
    (notice: RecordingNotice): void => {
      const senderLabel = resolveRecordingSenderLabel(notice.sender);
      switch (notice.kind) {
        case "local_start":
          setRecordingNoticeVariant("start");
          showRecordingNotice(
            t("call_recorder.local_started_notice", { user: senderLabel }),
          );
          break;
        case "local_stop":
          setRecordingNoticeVariant("stop");
          showRecordingNotice(
            t("call_recorder.local_stopped_notice", { user: senderLabel }),
          );
          break;
        case "local_connection_lost":
          setRecordingNoticeVariant("stop");
          showRecordingNotice(t("call_recorder.connection_lost_notice"));
          break;
      }
    },
    [resolveRecordingSenderLabel, showRecordingNotice, t],
  );

  const onProtocolistNotice = useCallback(
    (notice: ProtocolistNotice): void => {
      switch (notice.kind) {
        case "protocolist_connecting":
          setRecordingNoticeVariant("start");
          showRecordingNotice(t("protocolist.connecting_notice"));
          break;
        case "protocolist_connected":
          setRecordingNoticeVariant("start");
          showRecordingNotice(t("protocolist.connected_notice"));
          break;
        case "protocolist_disconnected":
          setRecordingNoticeVariant("stop");
          showRecordingNotice(t("protocolist.disconnected_notice"));
          break;
        case "protocolist_failed":
          setRecordingNoticeVariant("stop");
          // Причина есть только у отказов при нажатии кнопки. Внешний статус
          // failed от Matrix приходит без неё — там прежняя общая фраза.
          showRecordingNotice(
            notice.reason
              ? t("protocolist.unavailable_notice", {
                  reason: t(
                    protocolistUnavailableReasonKeys[notice.reason],
                  ),
                })
              : t("protocolist.failed_notice"),
          );
          break;
      }
    },
    [showRecordingNotice, t],
  );

  return {
    recordingNoticeText,
    recordingNoticeVariant,
    onRecordingNotice,
    onProtocolistNotice,
  };
}
